import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Users, TrendingUp, CheckCircle, Target, PhoneCall, Clock,
  Star, Eye, Search, Activity, ArrowLeft, RefreshCw
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { dashboardService, masterService, leadService, courseService } from '../../services/api';
import { KPICard, Skeleton, EmptyState, Modal, Badge, Tab } from '../../components/ui';
import { thStyle, tdStyle } from '../../utils/tableStyles'; // used in CounsellorDetail call-history table
import DashboardDateFilter from '../dashboard/DashboardDateFilter';
import LeadDetailView from '../leads/LeadDetailView';
import { STATUS_COLORS, STATUS_NAMES, PRIORITIES } from '../../constants';

const PAGE_SIZE = 20;

const CURRENT_YEAR  = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth();

const defaultParams = () => {
  const start = new Date(CURRENT_YEAR, CURRENT_MONTH, 1);
  const end   = new Date(CURRENT_YEAR, CURRENT_MONTH + 1, 0, 23, 59, 59);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate:   end.toISOString().split('T')[0],
  };
};

// Compact table styles that override the shared thStyle/tdStyle
// (which hard-code whiteSpace:'nowrap' — wrong for a wide dynamic table)
const cTh = (dark, extra = {}) => ({
  padding: '7px 6px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: dark ? '#94A3B8' : '#64748B',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  verticalAlign: 'bottom',
  lineHeight: 1.3,
  background: 'inherit',
  ...extra,
});

const sTh = (dark) => ({          // status column header — narrower + centred
  padding: '6px 3px',
  textAlign: 'center',
  fontSize: 10,
  fontWeight: 600,
  color: dark ? '#94A3B8' : '#64748B',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  verticalAlign: 'bottom',
  lineHeight: 1.25,
  minWidth: 50,
  background: 'inherit',
});

const cTd = (dark, extra = {}) => ({
  padding: '8px 6px',
  color: dark ? '#CBD5E1' : '#334155',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  verticalAlign: 'middle',
  ...extra,
});

const sTd = (dark, extra = {}) => ({  // status column cell — centred, no padding waste
  padding: '8px 3px',
  textAlign: 'center',
  color: dark ? '#CBD5E1' : '#334155',
  verticalAlign: 'middle',
  ...extra,
});

const fmtDuration = (secs) => {
  if (!secs) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}m ${s}s`;
};

const convColor = (rate) => {
  const r = parseFloat(rate);
  if (r >= 15) return '#10B981';
  if (r >= 8)  return '#F59E0B';
  return '#EF4444';
};

// ── Counsellor Detail Modal ───────────────────────────────────────────────────
const CounsellorDetail = ({ dark, detail, counsellor, leadStatuses, feedbackMap }) => {
  const total     = detail.assignedLeads || 0;
  const followUps = detail.followUps || {};

  // statusBreakdown comes as array [{ _id: code, count }] from getCounsellorPerformance
  const statusMap = useMemo(() =>
    (detail.statusBreakdown || []).reduce((acc, s) => ({ ...acc, [s._id]: s.count }), {}),
    [detail.statusBreakdown]
  );

  const recentCalls = detail.recentCalls || [];

  const cs = {
    background: dark ? '#0F172A' : '#F8FAFC',
    borderRadius: 12,
    padding: '14px 16px',
    border: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}`,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Counsellor identity */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: 'linear-gradient(135deg,#3B82F6,#8B5CF6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, fontWeight: 700, color: '#fff',
        }}>
          {(counsellor?.firstName?.[0] || '?').toUpperCase()}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: dark ? '#F1F5F9' : '#0F172A' }}>
            {counsellor?.firstName} {counsellor?.lastName || ''}
          </div>
          <div style={{ fontSize: 12, color: dark ? '#64748B' : '#94A3B8' }}>
            {counsellor?.employeeId || counsellor?.email}
          </div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <Badge color={counsellor?.status === 'active' ? '#10B981' : '#EF4444'}>
            {counsellor?.status || 'active'}
          </Badge>
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 12 }}>
        <KPICard icon={Users}      label="Assigned Leads" value={total}                               color="#3B82F6" />
        <KPICard icon={TrendingUp} label="Conv. Rate"     value={`${detail.conversionRate || 0}%`}    color="#10B981" />
        <KPICard icon={PhoneCall}  label="Total Calls"    value={detail.totalCalls || 0}              color="#8B5CF6" />
        <KPICard icon={Star}       label="Perf. Points"   value={detail.performancePoints || 0}       color="#F59E0B" />
        <KPICard icon={Clock}      label="Follow-up Rate" value={`${followUps.completionRate || 0}%`} color="#06B6D4" />
      </div>

      {/* Lead pipeline — fully driven by DB lead statuses */}
      <div style={cs}>
        <h4 style={{ margin: '0 0 14px', fontSize: 13, fontWeight: 600, color: dark ? '#F1F5F9' : '#0F172A' }}>
          Lead Pipeline
        </h4>
        {leadStatuses.length === 0 ? (
          <EmptyState icon={Activity} title="No lead statuses configured" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {leadStatuses.map(status => {
              const count = statusMap[status.code] || 0;
              const pct   = total > 0 ? (count / total) * 100 : 0;
              const color = status.color || '#6B7280';
              return (
                <div key={status.code}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, color: dark ? '#CBD5E1' : '#475569' }}>
                      {/* colour dot from DB */}
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                      {status.name}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 700, color }}>
                      {count}&nbsp;
                      <span style={{ fontWeight: 400, color: dark ? '#64748B' : '#94A3B8' }}>
                        ({pct.toFixed(1)}%)
                      </span>
                    </span>
                  </div>
                  <div style={{ height: 6, borderRadius: 99, background: dark ? '#1E293B' : '#E2E8F0', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99, transition: 'width 0.4s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Follow-up stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        {[
          { label: 'Completed', value: followUps.completed || 0, color: '#10B981' },
          { label: 'Pending',   value: followUps.pending   || 0, color: '#F59E0B' },
          { label: 'Missed',    value: followUps.missed    || 0, color: '#EF4444' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ ...cs, textAlign: 'center' }}>
            <div style={{ fontSize: 26, fontWeight: 700, color }}>{value}</div>
            <div style={{ fontSize: 11, color: dark ? '#94A3B8' : '#64748B', marginTop: 2 }}>
              Follow-ups {label}
            </div>
          </div>
        ))}
      </div>

      {/* Call history — feedback badge driven by populated DB ref + feedbackMap fallback */}
      <div>
        <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600, color: dark ? '#F1F5F9' : '#0F172A' }}>
          Recent Call History {recentCalls.length > 0 && `(${recentCalls.length})`}
        </h4>
        {recentCalls.length === 0 ? (
          <div style={cs}><EmptyState icon={PhoneCall} title="No calls recorded in this period" /></div>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}`, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: dark ? '#0F172A' : '#F8FAFC' }}>
                  <th style={thStyle(dark)}>Lead</th>
                  <th style={thStyle(dark)}>Date</th>
                  <th style={thStyle(dark)}>Duration</th>
                  <th style={thStyle(dark)}>Feedback</th>
                  <th style={thStyle(dark)}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {recentCalls.map((call, i) => {
                  // Use populated object; fall back to feedbackMap keyed by _id
                  const fb = (typeof call.feedback === 'object' && call.feedback !== null)
                    ? call.feedback
                    : feedbackMap[String(call.feedback)];
                  return (
                    <tr key={i} style={{ borderBottom: `1px solid ${dark ? '#0F172A' : '#F1F5F9'}` }}>
                      <td style={tdStyle(dark)}>
                        <div style={{ fontWeight: 600 }}>{call.lead?.fullName || '—'}</div>
                        <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8' }}>
                          {call.lead?.leadId || ''}
                        </div>
                      </td>
                      <td style={{ ...tdStyle(dark), whiteSpace: 'nowrap' }}>
                        {new Date(call.createdAt).toLocaleDateString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })}
                      </td>
                      <td style={tdStyle(dark)}>{fmtDuration(call.callDuration)}</td>
                      <td style={tdStyle(dark)}>
                        {fb ? (
                          <span style={{
                            padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                            background: `${fb.color}22`, color: fb.color,
                            whiteSpace: 'nowrap',
                          }}>
                            {fb.name}
                          </span>
                        ) : (
                          <span style={{ color: dark ? '#475569' : '#94A3B8' }}>—</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle(dark), maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {call.remarks || <span style={{ color: dark ? '#475569' : '#94A3B8' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main View ─────────────────────────────────────────────────────────────────
const RetailOUCounsellorView = () => {
  const { dark } = useTheme();

  // Masters from DB
  const [leadStatuses,  setLeadStatuses]  = useState([]);
  const [callFeedbacks, setCallFeedbacks] = useState([]);
  const [mastersReady,  setMastersReady]  = useState(false);

  // Overview data
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [params,  setParams]  = useState(defaultParams);
  const [search,  setSearch]  = useState('');

  // Detail modal / view toggle
  const [selectedId,    setSelectedId]    = useState(null);
  const [detail,        setDetail]        = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Counsellor Leads sub-page states
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsSearch, setLeadsSearch] = useState('');
  const [leadsSearchInput, setLeadsSearchInput] = useState('');
  const [leadsFilterSource, setLeadsFilterSource] = useState('');
  const [activeStatusTab, setActiveStatusTab] = useState('');
  const [counsellorLeads, setCounsellorLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [viewLeadId, setViewLeadId] = useState(null);

  // Masters for lead progress view
  const [courses, setCourses] = useState([]);
  const [leadSources, setLeadSources] = useState([]);

  // Load lead statuses, call feedbacks, lead sources and courses from DB on mount
  useEffect(() => {
    Promise.all([
      masterService.getLeadStatuses(),
      masterService.getCallFeedbacks(),
      masterService.getLeadSources(),
      courseService.getAll({ limit: 200 }).catch(() => ({ data: { data: [] } })),
    ]).then(([sRes, fRes, srcRes, cRes]) => {
      setLeadStatuses(
        (sRes.data.data || [])
          .filter(s => s.isActive && !s.isDeleted)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      );
      setCallFeedbacks(
        (fRes.data.data || [])
          .filter(f => f.isActive && !f.isDeleted)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      );
      setLeadSources(srcRes.data.data || []);
      setCourses(cRes.data.data || []);
    }).catch(console.error)
      .finally(() => setMastersReady(true));
  }, []);

  // Fetch counsellors overview whenever date range changes
  const fetchOverview = useCallback((p) => {
    setLoading(true);
    setData(null);
    dashboardService.getCounsellorOverview(p)
      .then(({ data: res }) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchOverview(params); }, [fetchOverview, params]);

  // Fetch counsellor detail when a row is selected or date filter changes
  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setDetailLoading(true);
    dashboardService.getCounsellorPerformance(selectedId, params)
      .then(({ data: res }) => setDetail(res.data))
      .catch(console.error)
      .finally(() => setDetailLoading(false));
  }, [selectedId, params]);

  // Fetch counsellor's leads for sub-page
  useEffect(() => {
    if (!selectedId) {
      setCounsellorLeads([]);
      setLeadsTotal(0);
      return;
    }

    const fetchLeads = async () => {
      setLeadsLoading(true);
      try {
        const queryParams = {
          counsellor: selectedId,
          startDate: params.startDate,
          endDate: params.endDate,
          page: leadsPage,
          limit: PAGE_SIZE,
          search: leadsSearch || undefined,
          leadSource: leadsFilterSource || undefined,
          statusCode: activeStatusTab || undefined,
        };
        const { data: res } = await leadService.getAll(queryParams);
        setCounsellorLeads(res.data || []);
        setLeadsTotal(res.total || 0);
      } catch (err) {
        console.error('Error loading counsellor leads:', err);
      } finally {
        setLeadsLoading(false);
      }
    };

    fetchLeads();
  }, [selectedId, params, leadsPage, leadsSearch, leadsFilterSource, activeStatusTab]);

  // Reset filters when changing active counsellor
  useEffect(() => {
    setLeadsPage(1);
    setLeadsSearch('');
    setLeadsSearchInput('');
    setLeadsFilterSource('');
    setActiveStatusTab('');
  }, [selectedId]);

  const leadsSearchTimer = useRef(null);

  const handleLeadsSearchChange = (val) => {
    setLeadsSearchInput(val);
    clearTimeout(leadsSearchTimer.current);
    leadsSearchTimer.current = setTimeout(() => {
      setLeadsSearch(val);
      setLeadsPage(1);
    }, 400);
  };

  // Lookup map: feedbackId → { name, color } — used as fallback in call history
  const feedbackMap = useMemo(
    () => Object.fromEntries(callFeedbacks.map(f => [String(f._id), f])),
    [callFeedbacks]
  );

  // Client-side search filter
  const counsellors = useMemo(() => {
    const list = data?.counsellors || [];
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(c =>
      `${c.firstName} ${c.lastName || ''}`.toLowerCase().includes(q) ||
      (c.employeeId || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const summary            = data?.summary || {};
  const selectedCounsellor = data?.counsellors?.find(c => String(c._id) === String(selectedId));
  const isLoading          = loading || !mastersReady;

  // Theme tokens
  const txtPrimary   = dark ? '#F1F5F9' : '#0F172A';
  const txtSecondary = dark ? '#94A3B8' : '#64748B';
  const cardBg       = dark ? '#1E293B' : '#fff';
  const border       = dark ? '#334155' : '#E2E8F0';
  const inputBg      = dark ? '#1E293B' : '#F8FAFC';
  const rowHoverBg   = dark ? '#0F172A' : '#F8FAFC';

  const statusCounts = useMemo(() => {
    if (!detail?.statusBreakdown) return {};
    return detail.statusBreakdown.reduce((acc, curr) => {
      acc[curr._id] = curr.count;
      return acc;
    }, {});
  }, [detail]);

  const leadsTabs = useMemo(() => {
    const tabs = [{ key: '', label: `All Leads (${detail?.assignedLeads || 0})` }];
    leadStatuses.forEach(status => {
      const count = statusCounts[status.code] || 0;
      tabs.push({
        key: status.code,
        label: `${status.name} (${count})`,
      });
    });
    return tabs;
  }, [leadStatuses, statusCounts, detail]);

  const totalLeadsPages = Math.ceil(leadsTotal / PAGE_SIZE);

  if (selectedId) {
    return (
      <div>
        {/* Header ─────────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => setSelectedId(null)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                border: `1px solid ${border}`, background: dark ? '#1E293B' : '#fff',
                color: '#3B82F6', cursor: 'pointer', outline: 'none',
              }}
            >
              <ArrowLeft size={16} /> Back
            </button>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: txtPrimary, margin: 0 }}>
                {selectedCounsellor ? `${selectedCounsellor.firstName} ${selectedCounsellor.lastName || ''}` : 'Counsellor View'}
              </h1>
              <p style={{ fontSize: 13, color: txtSecondary, margin: '4px 0 0' }}>
                {selectedCounsellor?.employeeId ? `ID: ${selectedCounsellor.employeeId} · ` : ''}
                {selectedCounsellor?.email || ''}
              </p>
            </div>
          </div>
          <DashboardDateFilter onChange={p => setParams(p)} />
        </div>

        {detailLoading ? (
          <Skeleton rows={8} cols={4} />
        ) : (
          <>
            {/* Summary KPI cards ──────────────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 14, marginBottom: 24 }}>
              <KPICard icon={Target}      label="Total Assigned Leads" value={detail?.assignedLeads || 0}        color="#8B5CF6" />
              <KPICard icon={TrendingUp}  label="Conversion Rate"     value={`${detail?.conversionRate || 0}%`} color="#10B981" />
              <KPICard icon={PhoneCall}   label="Total Calls"         value={detail?.totalCalls || 0}           color="#3B82F6" />
              <KPICard icon={Clock}       label="Follow-up Rate"      value={`${detail?.followUps?.completionRate || 0}%`} color="#06B6D4" />
            </div>

            {/* Leads list table card ──────────────────────────────────────── */}
            <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, overflow: 'hidden' }}>
              {/* Status tabs */}
              <div style={{ padding: '16px 20px 0', borderBottom: `1px solid ${border}` }}>
                <Tab tabs={leadsTabs} active={activeStatusTab} onChange={setActiveStatusTab} />
              </div>

              {/* Toolbar filters */}
              <div style={{ display: 'flex', gap: 10, padding: '14px 20px', flexWrap: 'wrap', alignItems: 'center', borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
                {/* Search */}
                <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                  <Search size={14} color={dark ? '#475569' : '#94A3B8'} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
                  <input
                    value={leadsSearchInput}
                    onChange={e => handleLeadsSearchChange(e.target.value)}
                    placeholder="Search by name, mobile, lead ID..."
                    style={{
                      width: '100%', padding: '7px 10px 7px 30px', borderRadius: 8, fontSize: 13,
                      border: `1px solid ${border}`,
                      background: dark ? '#1E293B' : '#F8FAFC',
                      color: txtPrimary, outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* Lead Source filter */}
                <select
                  value={leadsFilterSource}
                  onChange={e => { setLeadsFilterSource(e.target.value); setLeadsPage(1); }}
                  style={{
                    padding: '7px 10px', borderRadius: 8, fontSize: 13,
                    border: `1px solid ${border}`,
                    background: dark ? '#1E293B' : '#F8FAFC',
                    color: txtPrimary, outline: 'none', cursor: 'pointer',
                  }}
                >
                  <option value="">All Lead Sources</option>
                  {leadSources.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>

                <button
                  onClick={() => { setLeadsSearch(''); setLeadsSearchInput(''); setLeadsFilterSource(''); setLeadsPage(1); }}
                  title="Reset filters"
                  style={{
                    padding: 8, borderRadius: 8, border: `1px solid ${border}`,
                    background: 'transparent', color: txtSecondary, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  <RefreshCw size={14} />
                </button>
              </div>

              {/* Table */}
              <div style={{ overflowX: 'auto' }}>
                {leadsLoading ? (
                  <div style={{ padding: 20 }}><Skeleton rows={6} cols={5} /></div>
                ) : counsellorLeads.length === 0 ? (
                  <div style={{ padding: 40 }}>
                    <EmptyState title="No leads found" description="Try adjusting your status tab or filters." />
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead style={{ background: dark ? '#0F172A' : '#F8FAFC' }}>
                      <tr>
                        <th style={thStyle(dark)}>Lead ID</th>
                        <th style={thStyle(dark)}>Name</th>
                        <th style={thStyle(dark)}>Mobile</th>
                        <th style={thStyle(dark)}>Email</th>
                        <th style={thStyle(dark)}>Course</th>
                        <th style={thStyle(dark)}>Source</th>
                        <th style={thStyle(dark)}>Status</th>
                        <th style={thStyle(dark)}>Priority</th>
                        <th style={thStyle(dark)}>Created</th>
                        <th style={thStyle(dark)}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {counsellorLeads.map(lead => (
                        <tr key={lead._id} style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
                          <td style={{ ...tdStyle(dark), fontFamily: 'monospace', fontSize: 11 }}>{lead.leadId}</td>
                          <td style={{ ...tdStyle(dark), fontWeight: 600 }}>{lead.fullName}</td>
                          <td style={tdStyle(dark)}>{lead.mobile}</td>
                          <td style={tdStyle(dark)}>{lead.email || '—'}</td>
                          <td style={tdStyle(dark)}>{lead.course?.name || '—'}</td>
                          <td style={tdStyle(dark)}>{lead.leadSource?.name || '—'}</td>
                          <td style={tdStyle(dark)}>
                            <Badge color={lead.currentStatus?.color || STATUS_COLORS[lead.statusCode] || '#6B7280'}>
                              {lead.currentStatus?.name || STATUS_NAMES[lead.statusCode] || lead.statusCode}
                            </Badge>
                          </td>
                          <td style={tdStyle(dark)}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: PRIORITIES[lead.priority] || '#6B7280', textTransform: 'capitalize' }}>
                              {lead.priority || '—'}
                            </span>
                          </td>
                          <td style={tdStyle(dark)}>
                            {new Date(lead.createdAt).toLocaleDateString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric',
                            })}
                          </td>
                          <td style={tdStyle(dark)}>
                            <button
                              onClick={() => setViewLeadId(lead._id)}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                border: `1px solid ${border}`, background: 'transparent',
                                color: '#3B82F6', cursor: 'pointer',
                              }}
                            >
                              <Eye size={12} /> View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination */}
              {totalLeadsPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: `1px solid ${dark ? '#334155' : '#E2E8F0'}` }}>
                  <span style={{ fontSize: 13, color: txtSecondary }}>
                    Showing {(leadsPage - 1) * PAGE_SIZE + 1} to {Math.min(leadsPage * PAGE_SIZE, leadsTotal)} of {leadsTotal} leads
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      disabled={leadsPage === 1}
                      onClick={() => setLeadsPage(p => p - 1)}
                      style={{
                        padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        border: `1px solid ${border}`, background: 'transparent',
                        color: leadsPage === 1 ? txtSecondary : '#3B82F6',
                        cursor: leadsPage === 1 ? 'not-allowed' : 'pointer',
                        opacity: leadsPage === 1 ? 0.5 : 1
                      }}
                    >
                      Previous
                    </button>
                    <button
                      disabled={leadsPage === totalLeadsPages}
                      onClick={() => setLeadsPage(p => p + 1)}
                      style={{
                        padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        border: `1px solid ${border}`, background: 'transparent',
                        color: leadsPage === totalLeadsPages ? txtSecondary : '#3B82F6',
                        cursor: leadsPage === totalLeadsPages ? 'not-allowed' : 'pointer',
                        opacity: leadsPage === totalLeadsPages ? 0.5 : 1
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Lead Details Modal */}
        <Modal open={!!viewLeadId} onClose={() => setViewLeadId(null)} title="Lead Details" width={800}>
          {viewLeadId && <LeadDetailView leadId={viewLeadId} />}
        </Modal>
      </div>
    );
  }

  return (
    <div>
      {/* Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: txtPrimary, margin: 0 }}>Team Performance</h1>
          <p style={{ fontSize: 13, color: txtSecondary, margin: '4px 0 0' }}>
            {data
              ? `${summary.totalCounsellors} counsellor${summary.totalCounsellors !== 1 ? 's' : ''} · assigned leads in selected period`
              : 'Loading team data…'}
          </p>
        </div>
        <DashboardDateFilter onChange={p => setParams(p)} />
      </div>

      {isLoading ? (
        <Skeleton rows={8} cols={4} />
      ) : !data ? (
        <EmptyState title="Failed to load team data" />
      ) : (
        <>
          {/* Summary KPI cards ──────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 14, marginBottom: 24 }}>
            <KPICard icon={Users}       label="Total Counsellors" value={summary.totalCounsellors || 0}        color="#3B82F6" />
            <KPICard icon={Target}      label="Total Leads"       value={summary.totalLeads || 0}              color="#8B5CF6" />
            <KPICard icon={CheckCircle} label="Total Admissions"  value={summary.totalAdmissions || 0}         color="#10B981" />
            <KPICard icon={TrendingUp}  label="Avg Conv. Rate"    value={`${summary.avgConversionRate || 0}%`} color="#F59E0B" />
          </div>

          {/* Counsellors table ──────────────────────────────────────────── */}
          <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, overflow: 'hidden' }}>

            {/* Toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderBottom: `1px solid ${border}`, flexWrap: 'wrap' }}>
              <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: txtPrimary, flex: 1 }}>
                Counsellors
                <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 400, color: txtSecondary }}>
                  ({counsellors.length}{search ? ` of ${data.counsellors.length}` : ''})
                </span>
              </h4>
              <div style={{ position: 'relative', width: 240 }}>
                <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: txtSecondary, pointerEvents: 'none' }} />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by name or ID…"
                  style={{ width: '100%', padding: '7px 10px 7px 30px', borderRadius: 8, fontSize: 13, border: `1px solid ${border}`, background: inputBg, color: txtPrimary, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            {counsellors.length === 0 ? (
              <div style={{ padding: 40 }}>
                <EmptyState icon={Users} title={search ? 'No matching counsellors' : 'No counsellors found'} />
              </div>
            ) : (
              /* overflowX: auto is a last-resort safety net only — the table
                 is designed to fit without scrolling through text-wrap + compact sizing */
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>

                  {/* Explicit column widths keep the table predictable */}
                  <colgroup>
                    <col style={{ width: 160 }} />   {/* Counsellor */}
                    <col style={{ width: 58 }} />    {/* Perf. Pts */}
                    <col style={{ width: 52 }} />    {/* Total Leads */}
                    {leadStatuses.map(s => <col key={s.code} style={{ width: 52 }} />)}
                    <col style={{ width: 46 }} />    {/* Calls */}
                    <col style={{ width: 58 }} />    {/* Conv. Rate */}
                    <col style={{ width: 60 }} />    {/* View */}
                  </colgroup>

                  <thead>
                    <tr style={{ background: dark ? '#0F172A' : '#F8FAFC' }}>
                      <th style={cTh(dark, { textAlign: 'left' })}>Counsellor</th>
                      <th style={cTh(dark, { textAlign: 'center' })}>Perf. Pts</th>
                      <th style={cTh(dark, { textAlign: 'center' })}>Leads</th>

                      {/* One column per DB lead status — header wraps freely */}
                      {leadStatuses.map(s => (
                        <th key={s.code} style={sTh(dark)}>
                          <span style={{
                            display: 'inline-block',
                            width: 6, height: 6, borderRadius: '50%',
                            background: s.color || '#6B7280',
                            marginBottom: 3,
                          }} />
                          <br />
                          {s.name}
                        </th>
                      ))}

                      <th style={cTh(dark, { textAlign: 'center' })}>Calls</th>
                      <th style={cTh(dark, { textAlign: 'center' })}>Conv.%</th>
                      <th style={cTh(dark)} />
                    </tr>
                  </thead>

                  <tbody>
                    {counsellors.map(c => (
                      <tr
                        key={c._id}
                        style={{ borderBottom: `1px solid ${dark ? '#0F172A' : '#F1F5F9'}` }}
                        onMouseEnter={e => (e.currentTarget.style.background = rowHoverBg)}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        {/* Counsellor — avatar initial + name wraps if long */}
                        <td style={cTd(dark)}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <div style={{
                              width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                              background: 'linear-gradient(135deg,#3B82F6,#8B5CF6)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 11, fontWeight: 700, color: '#fff',
                            }}>
                              {(c.firstName?.[0] || '?').toUpperCase()}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: 12, color: txtPrimary, lineHeight: 1.3 }}>
                                {c.firstName} {c.lastName || ''}
                              </div>
                              <div style={{ fontSize: 10, color: txtSecondary, marginTop: 1 }}>
                                {c.employeeId || c.email}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Performance points */}
                        <td style={cTd(dark, { textAlign: 'center' })}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#F59E0B', fontWeight: 700, fontSize: 12 }}>
                            <Star size={11} fill="#F59E0B" strokeWidth={0} />
                            {c.performancePoints}
                          </span>
                        </td>

                        {/* Total leads */}
                        <td style={cTd(dark, { textAlign: 'center', fontWeight: 700, color: '#3B82F6' })}>
                          {c.assignedLeads}
                        </td>

                        {/* Dynamic status counts */}
                        {leadStatuses.map(s => {
                          const count = c.statusBreakdown?.[s.code] || 0;
                          return (
                            <td key={s.code} style={sTd(dark, {
                              fontWeight: count > 0 ? 700 : 400,
                              color: count > 0 ? (s.color || txtSecondary) : (dark ? '#334155' : '#CBD5E1'),
                              fontSize: 12,
                            })}>
                              {count || '—'}
                            </td>
                          );
                        })}

                        {/* Calls */}
                        <td style={cTd(dark, { textAlign: 'center', color: '#06B6D4', fontWeight: 600 })}>
                          {c.totalCalls}
                        </td>

                        {/* Conversion rate */}
                        <td style={cTd(dark, { textAlign: 'center' })}>
                          <span style={{ fontWeight: 700, fontSize: 12, color: convColor(c.conversionRate) }}>
                            {c.conversionRate}%
                          </span>
                        </td>

                        {/* View */}
                        <td style={cTd(dark, { textAlign: 'center' })}>
                          <button
                            onClick={() => setSelectedId(String(c._id))}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                              border: `1px solid ${border}`, background: 'transparent',
                              color: '#3B82F6', cursor: 'pointer',
                            }}
                          >
                            <Eye size={12} /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default RetailOUCounsellorView;
